import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import Controllable from '../decorators/Controllable';
import UISelectableBox from './UISelectableBox';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { INPUT_FONT_SIZE } from 'HubStyleTokens/sizes';
import { FONT_FAMILIES, setFontSize, setFontSmoothing } from '../utils/Styles';
import deprecated from '../utils/propTypes/deprecated';
const SIZE_CLASSES = {
  auto: 'private-selectable-button--auto',
  sm: 'private-selectable-button--small',
  lg: 'private-selectable-button--large'
};
const TextContainer = styled.div.withConfig({
  displayName: "UISelectableButton__TextContainer",
  componentId: "icr6yh-0"
})(["width:100%;img + &&&{", "}", ""], ({
  size = 'sm'
}) => toShorthandSize(size) === 'lg' && css(["margin-bottom:8px;"]), ({
  size = 'sm'
}) => toShorthandSize(size) === 'lg' && css(["padding-top:16px;"]));
const Title = styled.span.withConfig({
  displayName: "UISelectableButton__Title",
  componentId: "icr6yh-1"
})(["width:100%;", ";", ";", ";display:block;white-space:normal;"], FONT_FAMILIES.demibold, setFontSmoothing(), setFontSize(INPUT_FONT_SIZE));
const Description = styled.span.withConfig({
  displayName: "UISelectableButton__Description",
  componentId: "icr6yh-2"
})(["", ";display:block;white-space:normal;", " + &{margin-top:4px;}"], setFontSize('14px'), Title);

function UISelectableButton(_ref) {
  let {
    children,
    className,
    description,
    name,
    size = 'sm',
    textLabel,
    title,
    type = 'default',
    truncatable = true
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "description", "name", "size", "textLabel", "title", "type", "truncatable"]);

  if (process.env.NODE_ENV !== 'production') {
    if ( // Kept `textLabel` to keep warnings concise, the one below is more actionable. Remove `textLabel` case once fully deprecated and removed.
    textLabel == null && title == null && description == null && children == null) {
      devLogger.warn({
        message: 'UISelectableButton: Should provide either text through title and description, or children.',
        key: 'UISelectableButton: content'
      });
    }

    if (textLabel) {
      devLogger.warn({
        message: `UISelectableButton: The \`textLabel\` prop is deprecated. Use the \`title\` and \`description\` props instead.`,
        key: 'UISelectableButton-textLabel'
      });
    }

    if (type === 'radio' && !name) {
      devLogger.warn({
        message: 'UISelectableButton: If type is set to radio you must provide a name',
        key: 'UISelectableButton: no name value'
      });
    }
  }

  const shorthandSize = toShorthandSize(size);
  const maybeSelectionMarkClass = shorthandSize === 'lg' ? '' : `private-selectable-button__label--${type === 'default' ? 'no-selection-mark' : 'selection-mark'}`;
  let computedSelectionMarkLocation;

  if (type !== 'default') {
    computedSelectionMarkLocation = shorthandSize === 'lg' ? 'top-right' : 'left';
  }

  const renderWithTextLabel = () => /*#__PURE__*/_jsx("span", {
    className: classNames(`private-selectable-button__label private-selectable-button__label--${!truncatable ? 'not-' : ''}truncatable`, maybeSelectionMarkClass),
    children: textLabel
  });

  const renderTitleAndDescription = () => /*#__PURE__*/_jsxs(TextContainer, {
    className: classNames(maybeSelectionMarkClass),
    size: size,
    children: [title && /*#__PURE__*/_jsx(Title, {
      size: size,
      children: title
    }), description && /*#__PURE__*/_jsx(Description, {
      size: size,
      className: "is--text--help",
      children: description
    })]
  });

  return /*#__PURE__*/_jsxs(UISelectableBox, Object.assign({
    className: classNames('private-selectable-button', SIZE_CLASSES[shorthandSize], className),
    innerClassName: 'private-selectable-button__inner' + (truncatable ? " private-selectable-button__inner--truncatable" : ""),
    name: type === 'default' ? undefined : name,
    padded: false,
    selectionMarkLocation: computedSelectionMarkLocation,
    type: type
  }, rest, {
    children: [children, textLabel ? renderWithTextLabel() : null, !textLabel && (title || description) && renderTitleAndDescription()]
  }));
}

const propTypes = {
  'aria-checked': PropTypes.bool,
  block: PropTypes.bool,
  children: PropTypes.node,
  description: PropTypes.node,
  disabled: PropTypes.bool.isRequired,
  name: PropTypes.string,

  /**
   * A `Controllable` function that is called when the `selected` value changes
   * in non-`radio` `type`s. To control `radio`s, use `onClick` instead.
   */
  onSelectedChange: PropTypes.func,
  readOnly: PropTypes.bool,
  selected: PropTypes.bool,
  size: PropTypes.oneOfType([propTypeForSizes(['sm', 'lg']), PropTypes.oneOf(['auto'])]).isRequired,
  // @ts-expect-error deprecated `message` and `defaultValue` are required arguments
  textLabel: deprecated(PropTypes.node),
  title: PropTypes.node,
  truncatable: PropTypes.bool,
  type: PropTypes.oneOf(['default', 'checkbox', 'radio']).isRequired
};
UISelectableButton.propTypes = propTypes;
const defaultProps = {
  disabled: false,
  readOnly: false,
  selected: false,
  size: 'sm',
  truncatable: true,
  type: 'default'
};
UISelectableButton.defaultProps = defaultProps;
UISelectableButton.displayName = 'UISelectableButton';
export default Controllable(UISelectableButton, ['selected']);